/**
 * 提交历史数据 Hook
 * 用于获取和管理工作流的提交历史记录
 * 支持未完成任务的自动轮询
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type { SubmissionRecord } from '@/types/submission'
import type { BatchTaskListItem } from '@/types/task'
import { BATCH_TASK_STATUS } from '@/types/task'
import { getWorkflowBatchTasks } from '@/services/taskService'

/**
 * 轮询配置
 */
const POLLING_CONFIG = {
  /** 轮询间隔（毫秒）- 5秒 */
  INTERVAL: 5000,
} as const

/**
 * 已完成的状态列表（不需要轮询）
 */
const FINISHED_STATUSES = [
  BATCH_TASK_STATUS.COMPLETED,
  BATCH_TASK_STATUS.PARTIAL_FAILED,
  BATCH_TASK_STATUS.FAILED,
  BATCH_TASK_STATUS.PAUSED,
  BATCH_TASK_STATUS.CANCELLED,
] as const

/**
 * Hook 返回类型
 */
export interface UseSubmissionHistoryReturn {
  /** 提交记录列表 */
  submissions: SubmissionRecord[]
  /** 是否正在加载 */
  isLoading: boolean
  /** 错误信息 */
  error: Error | null
  /** 记录总数 */
  total: number
  /** 刷新数据 */
  refresh: () => void
}

/**
 * 将 BatchTaskListItem 转换为 SubmissionRecord
 * @param item - 批量任务列表项
 * @returns 提交记录
 */
function toSubmissionRecord(item: BatchTaskListItem): SubmissionRecord {
  const processed = item.completed_count + item.failed_count
  const remaining = item.total_count - processed

  // 根据状态计算排队中和运行中的数量
  let queued = 0
  let running = 0
  if (item.status === BATCH_TASK_STATUS.PENDING) {
    queued = remaining
  } else if (item.status === BATCH_TASK_STATUS.RUNNING) {
    running = remaining
  }

  const record: SubmissionRecord = {
    id: item.id,
    workflow_id: item.workflow_id,
    remark: item.remark,
    task_mode: item.task_mode,
    stats: {
      queued,
      running,
      failed: item.failed_count,
      completed: item.completed_count,
      total: item.total_count,
    },
    submitted_at: item.created_at,
    created_at: item.created_at,
    updated_at: item.created_at,
  }

  // 只在有值时添加 scheduled_at（满足 exactOptionalPropertyTypes）
  if (item.scheduled_at !== undefined) {
    record.scheduled_at = item.scheduled_at
  }

  // 传递操作者信息（仅主账号查询时有值）
  if (item.operator_id !== undefined) {
    record.operator_id = item.operator_id
  }
  if (item.operator_username !== undefined) {
    record.operator_username = item.operator_username
  }
  if (item.operator_avatar !== undefined) {
    record.operator_avatar = item.operator_avatar
  }
  if (item.is_sub_account !== undefined) {
    record.is_sub_account = item.is_sub_account
  }

  return record
}

/**
 * 提交历史数据 Hook
 * @param workflowId - 工作流 ID
 * @param pageSize - 每页数量（默认 10）
 * @param enabled - 是否启用请求（默认 true，未登录时应传 false）
 * @returns 提交历史数据和状态
 */
export function useSubmissionHistory(
  workflowId: string | undefined,
  pageSize = 10,
  enabled = true
): UseSubmissionHistoryReturn {
  const [items, setItems] = useState<BatchTaskListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [total, setTotal] = useState(0)

  // 轮询定时器
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  /**
   * 检查列表中是否有未完成的任务
   */
  const hasIncompleteTask = useMemo((): boolean => {
    return items.some(
      (item) => !FINISHED_STATUSES.includes(item.status as typeof FINISHED_STATUSES[number])
    )
  }, [items])

  /**
   * 停止轮询
   */
  const stopPolling = useCallback((): void => {
    if (pollingTimerRef.current !== null) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  /**
   * 获取提交历史数据
   * @param showLoading - 是否显示加载状态
   */
  const fetchSubmissions = useCallback(
    async (showLoading = true): Promise<void> => {
      if (workflowId === undefined || !enabled) {
        return
      }

      if (showLoading) {
        setIsLoading(true)
      }
      setError(null)

      try {
        const response = await getWorkflowBatchTasks(workflowId, 1, pageSize)

        if (!isMountedRef.current) return

        setItems(response.items)
        setTotal(response.total)
      } catch (err) {
        if (!isMountedRef.current) return
        const errorMessage =
          err instanceof Error ? err.message : '获取提交历史失败'
        setError(new Error(errorMessage))
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [workflowId, pageSize, enabled]
  )

  /**
   * 安排下一次轮询
   */
  const schedulePolling = useCallback((): void => {
    // 清除已有的定时器
    stopPolling()

    pollingTimerRef.current = setTimeout(() => {
      void fetchSubmissions(false) // 轮询时不显示 loading
    }, POLLING_CONFIG.INTERVAL)
  }, [fetchSubmissions, stopPolling])

  /**
   * 刷新数据
   */
  const refresh = useCallback((): void => {
    stopPolling()
    void fetchSubmissions(true)
  }, [fetchSubmissions, stopPolling])

  // 初始加载
  useEffect(() => {
    isMountedRef.current = true
    void fetchSubmissions(true)

    return (): void => {
      isMountedRef.current = false
      stopPolling()
    }
  }, [fetchSubmissions, stopPolling])

  // 根据任务状态启动或停止轮询
  useEffect(() => {
    if (hasIncompleteTask && isMountedRef.current) {
      schedulePolling()
    } else {
      stopPolling()
    }

    return stopPolling
  }, [hasIncompleteTask, schedulePolling, stopPolling])

  // 转换为 SubmissionRecord 格式
  const submissions = useMemo(
    () => items.map(toSubmissionRecord),
    [items]
  )

  return {
    submissions,
    isLoading,
    error,
    total,
    refresh,
  }
}
